import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { JobInfo, JobType, ServerErrorResponse } from '@blaze-shared/api-contracts-domain';
import { BlazeTableSchema } from '@blaze-shared/schema-domain';

import { FakeJobsApiService } from './fake-jobs-api.service';
import { JobMonitorService } from './job-monitor.service';
import { JobsApiService } from './jobs-api.service';

@Injectable({ providedIn: 'root' })
export class JobsFacade {
  private readonly jobsApiService = inject(JobsApiService);
  private readonly fakeJobsApiService = inject(FakeJobsApiService);
  private readonly jobMonitorService = inject(JobMonitorService);

  getAccountJobs(accountId: string): Observable<JobInfo<unknown>[]> {
    return this.jobsApiService.getJobs({ accountId });
  }

  getProjectJobs(projectId: string): Observable<JobInfo<unknown>[]> {
    return this.jobsApiService.getJobs({ projectId });
  }

  getWorkflowJobs(workflowId: string): Observable<JobInfo<unknown>[]> {
    return this.jobsApiService.getJobs({ workflowId });
  }

  getDatasetJobs(datasetId: string): Observable<JobInfo<BlazeTableSchema>[]> {
    return this.jobsApiService.getJobs({ datasetId });
  }

  getJobsStatus(jobIds: string | string[]): Observable<JobInfo<unknown>[]> {
    const ids = Array.isArray(jobIds) ? jobIds : [jobIds];
    return this.jobsApiService.postJobsStatus(ids);
  }

  cancelJob(jobId: string): Observable<unknown> {
    return this.jobsApiService.postCancelJob(jobId);
  }

  addJobsToMonitor(jobIds: string[]): void {
    this.jobMonitorService.addJobsToMonitor(jobIds);
  }

  monitorJob<T = unknown>(jobId: string): Observable<JobInfo<T> | undefined> {
    return this.jobMonitorService.monitorJob<T>(jobId);
  }

  stopMonitoring(jobId: string | string[]): void {
    this.jobMonitorService.stopMonitoring(jobId);
  }

  observeJobCompletion<T = unknown>(jobType: JobType): Observable<JobInfo<T>[]> {
    return this.jobMonitorService.observeJobCompletion<T>(jobType);
  }

  pollJobStatusUntilDoneOrStopped<T>(
    jobId?: string | null,
    pollTime?: number,
    cancelOnUnsubscribe?: boolean,
    statusCallback?: (info: JobInfo<T> | undefined) => void,
  ): Observable<T | ServerErrorResponse> {
    return this.jobMonitorService.pollJobStatusUntilDoneOrStopped<T>(
      jobId,
      pollTime,
      cancelOnUnsubscribe,
      statusCallback,
    );
  }
}
